import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";
import { Button } from "@/shadcnComponents/ui/button";
import { Rewind } from "lucide-react";
import { PiDownloadSimpleBold, PiPlayBold } from "react-icons/pi";

import { useEffect, useRef, useState } from "react";
import { useGeneralSettings } from "@/Providers/General";
import {
  Wrapped2024TotalWords,
  Wrapped2024FavorableMonth,
  Wrapped2024TotalImpressions,
  Wrapped2024FavorableDayOfWeek,
  Wrapped2024LongestStreak,
  Wrapped2024TotalLikes,
  Wrapped2024TotalComments,
  Wrapped2024MostEngagedPostType,
  Wrapped2024MostFavorableDay,
  Wrapped2024MonthlyTrend,
} from "./Components/YearWrap2024Cards";
import { SheetTriggerForWrap2024 } from "./Components/YearWrapComponents";
import { useInfluencer, YearWrap2024MetricsData } from "@/Providers/Influencer";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import HeartLoader from "@/Components/Loader/heatLoader";
import mixpanel from "mixpanel-browser";

export const YearWrap2024 = () => {
  const generalState = useGeneralSettings();
  const influencerState = useInfluencer();
  const { toast } = useToast();

  //states
  const [influencerYearWrap, setInfluencerYearWrap] =
    useState<YearWrap2024MetricsData>();

  const totalWordsRef = useRef<HTMLDivElement>(null);
  const favorableMonthRef = useRef<HTMLDivElement>(null);
  const totalImpressionsRef = useRef<HTMLDivElement>(null);
  const favorableDayOfWeekRef = useRef<HTMLDivElement>(null);
  const longestStreakRef = useRef<HTMLDivElement>(null);
  const totalLikesRef = useRef<HTMLDivElement>(null);
  const totalCommentsRef = useRef<HTMLDivElement>(null);
  const mostEngagedPostTypeRef = useRef<HTMLDivElement>(null);
  const mostFavorableDayRef = useRef<HTMLDivElement>(null);
  const monthlyTrendRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getYearWrap2024 = async () => {
      generalState?.setHeartLoading(true);
      const result = await influencerState?.getYearWrap2024();
      if (result?.success) {
        setInfluencerYearWrap(result.result);

        //add synchronous delay of 5 seconds for loader
        await new Promise((resolve) => setTimeout(resolve, 2000));

        generalState?.setHeartLoading(false);
      } else {
        generalState?.setHeartLoading(false);
        toast({
          title: "Something went wrong",
          description: result.message,
        });
      }
    };
    getYearWrap2024();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPDF = async () => {
    generalState?.setHeartLoading(true);
    try {
      const currentPageUrl = window.location.href;

      const classNames = [
        "year_Wrap_Item_Total_Impressions",
        "year_Wrap_Item_Longest_Streak",
        "year_Wrap_Item_Total_Words",
        "year_Wrap_Item_Favorable_Month",
        "year_Wrap_Item_Favorable_Day_Of_Week",
        "year_Wrap_Item_Total_Likes",
        "year_Wrap_Item_Total_Comments",
        "year_Wrap_Item_Most_Engaged_Post_Type",
        "year_Wrap_Item_Most_Favorable_Day",
        "year_Wrap_Item_Monthly_Trend",
      ];

      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_SERVER_URL}/ext/download/pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            jwtToken: localStorage.getItem("jwtToken"),
          },
          body: JSON.stringify({ pageUrl: currentPageUrl, classNames }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "exclusive-linkedin-unwrapped-by-anchors.pdf";
      link.click();

      URL.revokeObjectURL(url);
      generalState?.setHeartLoading(false);
    } catch (error) {
      generalState?.setHeartLoading(false);
      console.error("Error generating PDF:", error);
      toast({
        title: "Error generating PDF",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };
  return (
    <>
      <MobileMenuBar />
      <div className="w-full relative">
        {generalState?.HeartLoading && <HeartLoader />}
        <div className="w-full relative  px-7 py-9 mb-[100px] ">
          {/* header */}
          <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            {/* Heading and Subheading */}
            <div className="flex flex-col items-start gap-2">
              {/* Heading */}
              <div className="flex items-center gap-2">
                <Rewind size={24} color="#F9FBFF" />
                <h1 className="text-[#F9FBFF] text-center text-base md:text-xl not-italic font-semibold leading-[normal]">
                  2024 in Review - LinkedIn Unwrapped
                </h1>
              </div>
              {/* Subheading */}
              <h2 className="text-[#A6A6A6] text-xs md:text-sm not-italic font-medium leading-6">
                Relive your best Linkedin moments from 2024. See what made the
                year unforgettable
              </h2>
              <span className="text-[#ef4444] text-xs md:text-xs italic font-medium leading-6">
                Encountering an error in your Wrapped 2024? Click{" "}
                <b>Sync Profile</b>
                (top-right corner) to update your profile for a better
                experience.
              </span>
            </div>

            {/* Buttons */}
            <div className="w-full sm:w-auto grid grid-cols-2 sm:flex items-center gap-3">
              <SheetTriggerForWrap2024
                onDownlaodAll={downloadPDF}
                influencerYearWrap={influencerYearWrap}
                triggerElement={
                  <Button
                    className="bg-transparent border border-[color:var(--Natural-800,#424242)] rounded-[5px] border-solid text-[color:var(--Natural-100,#F5F5F5)] text-center text-xs not-italic font-semibold leading-[18px] gap-1.5 w-full"
                    variant="outline"
                  >
                    View All
                    <PiPlayBold size={14} />
                  </Button>
                }
              />

              <Button
                onClick={() => {
                  mixpanel.track("Year Wrap 2024: Download PDF: Clicked");
                  downloadPDF();
                }}
                className="gap-1.5 hover:bg-white hover:text-black w-full"
                variant="destructive"
              >
                Download PDF
                <PiDownloadSimpleBold size={14} />
              </Button>
            </div>
          </div>

          {/* cards display */}
          <div className="flex items-center justify-center  gap-[39px] flex-wrap w-full mt-8 ">
            {[
              <Wrapped2024TotalImpressions
                cardValue={influencerYearWrap?.metrics?.total_impressions}
                ref={totalImpressionsRef}
                containerClassName={`year_Wrap_Item_Total_Impressions`}
              />,

              <Wrapped2024LongestStreak
                cardValue={influencerYearWrap?.longest_streak?.duration}
                ref={longestStreakRef}
                startDate={influencerYearWrap?.longest_streak?.start_date}
                endDate={influencerYearWrap?.longest_streak?.end_date}
                containerClassName={`year_Wrap_Item_Longest_Streak`}
              />,

              <Wrapped2024TotalWords
                cardValue={influencerYearWrap?.metrics?.total_characters}
                ref={totalWordsRef}
                containerClassName={`year_Wrap_Item_Total_Words`}
              />,
              <Wrapped2024FavorableMonth
                cardValue={
                  influencerYearWrap?.most_favorable?.month?.month_number
                }
                heading={influencerYearWrap?.text?.month}
                ref={favorableMonthRef}
                containerClassName={`year_Wrap_Item_Favorable_Month`}
              />,

              <Wrapped2024FavorableDayOfWeek
                cardValue={
                  influencerYearWrap?.most_favorable?.day_of_week?.day_number
                }
                ref={favorableDayOfWeekRef}
                containerClassName={`year_Wrap_Item_Favorable_Day_Of_Week`}
              />,

              <Wrapped2024TotalLikes
                heading={influencerYearWrap?.text?.likes}
                cardValue={influencerYearWrap?.metrics?.total_likes}
                ref={totalLikesRef}
                containerClassName={`year_Wrap_Item_Total_Likes`}
              />,
              <Wrapped2024TotalComments
                heading={influencerYearWrap?.text?.comments}
                cardValue={influencerYearWrap?.metrics?.total_comments}
                ref={totalCommentsRef}
                containerClassName={`year_Wrap_Item_Total_Comments`}
              />,
              <Wrapped2024MostEngagedPostType
                cardValue={
                  influencerYearWrap?.most_engaged_posts?.[0]?.post_type
                }
                ref={mostEngagedPostTypeRef}
                containerClassName={`year_Wrap_Item_Most_Engaged_Post_Type`}
              />,
              <Wrapped2024MostFavorableDay
                impressions={
                  influencerYearWrap?.most_favorable?.day?.impressions
                }
                cardValue={influencerYearWrap?.most_favorable?.day?.date}
                ref={mostFavorableDayRef}
                containerClassName={`year_Wrap_Item_Most_Favorable_Day`}
              />,
              <Wrapped2024MonthlyTrend
                monthlyTrend={influencerYearWrap?.monthlyTrend}
                ref={monthlyTrendRef}
                heading={influencerYearWrap?.text?.monthlyTrendHeader}
                subheading={influencerYearWrap?.text?.monthlyTrendSubHeader}
                footerText={influencerYearWrap?.text?.monthlyTrendFooter}
                containerClassName={`year_Wrap_Item_Monthly_Trend`}
              />,
            ]?.map((item, index) => {
              return (
                <SheetTriggerForWrap2024
                  onDownlaodAll={downloadPDF}
                  influencerYearWrap={influencerYearWrap}
                  triggerElement={item}
                  key={`triggersWrap_${index}`}
                  startIndex={index}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
